import { useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  Stack,
  Typography,
} from "@mui/material";
import { ErrorOutline, ReportProblem } from "@mui/icons-material";
import { LoadingButton } from "@mui/lab";
import { useCustom } from "@refinedev/core";
import { Country, useMarketplaceStore } from "../../store/useMarketplaceStore";
import { useTranslation } from "../../hooks/useTranslation";
import { useStartReAuthorization } from "../../pages/authentication/useStartReAuthorization";

type StoreResponse = {
  marketplaceId: number;
  storeName: string;
  financeAuthorizationMissing?: boolean;
};

type StoresResponse = {
  marketplaces: StoreResponse[];
};

const DISMISSED_KEY = "financeAuthorizationDialogDismissed";
const REFETCH_INTERVAL_MS = 5 * 60 * 1000;

const RED = "#B71C1C";

// The backend sets `financeAuthorizationMissing` on a store when Amazon
// answers its financial-events call with 403: the seller's refresh token was
// granted before the app had the Finance role, so the seller has to authorize
// the store again on Amazon. Shown on every page: a red bar that cannot be
// closed, and a big red dialog that can be put off for this browser session.
export const FinanceAuthorizationAlert = () => {
  const { translate } = useTranslation("pages.stores.financeAuthorization");
  const countries = useMarketplaceStore((state) => state.countries);
  const { startReAuthorization, isLoading } = useStartReAuthorization();
  const [dismissed, setDismissed] = useState(
    () => sessionStorage.getItem(DISMISSED_KEY) === "true",
  );

  const { data } = useCustom<StoresResponse>({
    url: "store",
    method: "get",
    errorNotification: false,
    queryOptions: {
      queryKey: ["financeAuthorizationStores"],
      refetchInterval: REFETCH_INTERVAL_MS,
      refetchOnWindowFocus: true,
    },
  });

  const affected = useMemo<Country[]>(() => {
    const missingIds = new Set(
      (data?.data?.marketplaces ?? [])
        .filter((store) => store.financeAuthorizationMissing === true)
        .map((store) => store.marketplaceId),
    );
    return countries.filter((country) => missingIds.has(country.id));
  }, [data, countries]);

  if (affected.length === 0) return null;

  const storeLabel = (country: Country) =>
    `${country.storeName ?? ""} (${country.countryCode})`;

  const closeDialog = () => {
    sessionStorage.setItem(DISMISSED_KEY, "true");
    setDismissed(true);
  };

  return (
    <>
      <Box
        sx={{
          backgroundColor: RED,
          color: "#ffffff",
          px: 2,
          py: 1.25,
          mb: 2,
          borderRadius: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 1.5,
        }}
      >
        <Stack direction="row" alignItems="center" gap={1.5}>
          <ErrorOutline />
          <Typography fontWeight={700} sx={{ color: "inherit" }}>
            {translate("bannerText", {
              stores: affected.map(storeLabel).join(", "),
            })}
          </Typography>
        </Stack>
        <LoadingButton
          loading={isLoading}
          variant="contained"
          onClick={() => startReAuthorization(affected[0])}
          sx={{
            backgroundColor: "#ffffff",
            color: RED,
            fontWeight: 700,
            "&:hover": { backgroundColor: "#ffebee" },
          }}
        >
          {translate("bannerButton")}
        </LoadingButton>
      </Box>

      <Dialog
        open={!dismissed}
        onClose={closeDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { border: `4px solid ${RED}`, borderRadius: 2 } }}
      >
        <Box
          sx={{
            backgroundColor: RED,
            color: "#ffffff",
            textAlign: "center",
            py: 3,
            px: 2,
          }}
        >
          <ReportProblem sx={{ fontSize: 72 }} />
          <Typography variant="h4" fontWeight={800} sx={{ color: "inherit" }}>
            {translate("title")}
          </Typography>
        </Box>
        <DialogContent>
          <Stack gap={2}>
            <Typography variant="body1">{translate("description")}</Typography>
            <Typography variant="body1" fontWeight={700}>
              {translate("whatToDo")}
            </Typography>
            <Alert severity="error">{translate("sameAccountWarning")}</Alert>
            <Typography variant="h6">{translate("storesTitle")}</Typography>
            {affected.map((country) => (
              <Box
                key={country.id}
                sx={{
                  border: `1px solid ${RED}`,
                  borderRadius: 1,
                  p: 2,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 2,
                }}
              >
                <Box>
                  <Typography fontWeight={700}>{country.storeName}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {country.countryName} ({country.countryCode})
                  </Typography>
                </Box>
                <LoadingButton
                  loading={isLoading}
                  variant="contained"
                  color="error"
                  size="large"
                  onClick={() => startReAuthorization(country)}
                >
                  {translate("reAuthorizeButton")}
                </LoadingButton>
              </Box>
            ))}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog} color="inherit">
            {translate("later")}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
